import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { setSelectedDefinitionsForUnclassifiedST } from "../../../../../../../../../features/searchTermTabSlice";
import {
  formatNumber,
  formatTwoDecimal,
} from "../../../../../../../../../utils/helpers";
import dayjs from "dayjs";

// Example options for the autocomplete
const definitionOptions = [
  { label: "exclusive", value: "Exclusive" },
  { label: "allowed", value: "Allowed" },
  { label: "negative", value: "Negative" },
];

const OtherKeywordST = ({ open, onClose }: any) => {
  const dispatch = useDispatch();
  const {
    otherKeywordsForUnclassifiedST,
    otherKeywordRelatedToSearchTerm,
    selectedDefinitionsForUnclassifiedST,
  } = useSelector((state: any) => state.searchTermTab);
  // --- Define columns ---
  const columns: GridColDef[] = [
    {
      field: "keyword_name",
      headerName: "Keyword Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "keyword_matchtype",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "adgroup_name",
      headerName: "Adgroup Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "last_seen",
      headerName: "Last Seen",
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? dayjs(params.value).format("DD/MM/YYYY") : ""}
        </Typography>
      ),
    },
    {
      field: "impression_last_30_days",
      headerName: "IMP",
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatNumber(params.value) : ""}
        </Typography>
      ),
    },
    {
      field: "clicks_last_30_days",
      headerName: "Clicks",
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatNumber(params.value) : ""}
        </Typography>
      ),
    },
    {
      field: "cost_last_30_days",
      headerName: "Cost",
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatTwoDecimal(params.value, "$")}
        </Typography>
      ),
    },
    {
      field: "conversions_last_30_days",
      headerName: "CVR (%)",
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatTwoDecimal(params.value, "%")}
        </Typography>
      ),
    },
    {
      field: "Definition",
      headerName: "Definition",
      flex: 0.8,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Autocomplete
          data-tracker="544"
          size="small"
          options={definitionOptions.map((d) => d.value)} // array of strings
          value={
            selectedDefinitionsForUnclassifiedST[params.row.id]?.action || ""
          }
          onChange={(_, newValue) => {
            const updated = {
              ...selectedDefinitionsForUnclassifiedST,
              [params.row.id]: {
                ...params.row, // spread all row data
                action: newValue || null, // add the selected action
              },
            };
            dispatch(setSelectedDefinitionsForUnclassifiedST(updated));
          }}
          sx={{ width: "100%" }}
          renderInput={(paramsInput) => (
            <TextField
              {...paramsInput}
              placeholder="Select..."
              variant="standard"
              color="secondary"
              InputProps={{
                ...paramsInput.InputProps,
                disableUnderline: true, // 🔑 hides underline
              }}
              onKeyDown={(e) => {
                // Prevent all keyboard input
                e.preventDefault();
              }}
            />
          )}
          // Show clear icon only when there's a selected value
          clearIcon={
            selectedDefinitionsForUnclassifiedST[params.row.id]?.action
              ? undefined // Use default clear icon
              : null // Hide clear icon when no value
          }
        />
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Other Keywords Related to the Search term:{" "}
        {otherKeywordRelatedToSearchTerm}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: "100%", display: "grid" }}>
          <DataGridPro
            sx={dataGridStyles}
            density="compact"
            hideFooter
            //   disableMultipleRowSelection
            pagination={false}
            rows={otherKeywordsForUnclassifiedST}
            columns={columns}
            disableColumnMenu
            disableColumnFilter
            disableColumnReorder
            sortingOrder={["asc", "desc"]}
            isCellEditable={() => false}
            disableRowSelectionOnClick
            initialState={{
              pinnedColumns: {
                left: ["keyword_name", "keyword_matchtype"],
                right: ["Definition"],
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "0px 20px 16px" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            mt: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "left", maxWidth: 600 }}
          >
            * Select a definition for the keyword and close this dialog. The{" "}
            <strong>Save Changes</strong> button at the{" "}
            <strong>bottom-right</strong> of the table will be enabled
            automatically — click it to apply your changes.
          </Typography>
        </Box>
        <Button color="secondary" variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OtherKeywordST;

// === Shared DataGrid Styles ===
const dataGridStyles = {
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
    { outline: "none" },
  "& .MuiDataGrid-cellEmpty": {
    width: "auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  // row selection only once
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "rgba(156, 39, 176, 0.08) !important",
    "&:hover": {
      backgroundColor: "rgba(156, 39, 176, 0.16) !important",
    },
  },

  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft": {
    backgroundColor: "#f0def3 !important",
  },
  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft": {
    backgroundColor: "#e6d6e9ff !important",
  },

  // pinned RIGHT
  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight": {
    backgroundColor: "#f0def3 !important",
  },
  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight": {
    backgroundColor: "#e6d6e9ff !important",
  },
  // make pinned cells transparent → let row bg show through
  "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight": {
    backgroundColor: "#ffffff",
  },

  // remove extra blue tint on single cell select
  "& .MuiDataGrid-cell.Mui-selected": {
    backgroundColor: "inherit !important",
  },

  "& .MuiCheckbox-root": {
    color: "#9c27b0 !important",
  },
  "& .MuiCheckbox-root.Mui-checked": {
    color: "#9c27b0",
  },
  border: "none",
};
